// 네이버 CAPA «관리» 탭(P2b) — ① 수집 상태(켜기·끄기) · ② 네이버 계정 · ③ 설정 · ④ 알림 대상(CapaNotifyCard) · ⑤ 감사 기록.
// 운영(ops)은 읽기 전용(쓰기 입력·단추 disabled · 계정 힌트 없음) · 실제 차단은 서버(get_current_ops·get_current_admin).
// 🔴 오류는 response.data.detail 만 읽는다 — 요청 설정(config.data)에 비밀번호가 실린다. 오류 객체를 통째로
//    state·화면·글자로 넘기지 않는다.
// 🔴 계정 칸은 비제어 입력(ref) — 값을 state·DOM 속성에 두지 않는다 · 저장 요청이 끝나면(성공·실패·시한 초과) 비밀번호 칸을 비운다.
// 🔴 이 문서에 같은 출처 화면(임베드 앱·iframe)이 한 번이라도 붙었거나, 이 화면이 iframe 안이거나, 다른 창이 연 탭이면
//    계정 입력을 막는다 — 감시는 모듈이 앱이 뜰 때부터 한다(CapaPage 가 정적 import · 다시 마운트돼도 유지 · 새로고침해야 풀림).
// 🔴 설정은 쓰기 응답으로만 받는다(쓰기 뒤 다시 부르지 않는다) · 저장 직전 다시 받은 설정과 합쳐 남이 먼저 바꾼 칸이면 보내지 않는다.
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { capaAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, badge, btn, card, input } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';
import {
  DASH, SETTINGS_FIELDS, SETTINGS_LABELS, auditText, detailText, errorText, fmtSettingValue, mergeSettings,
  settingsDiff, settingsDraft, snapshotStatusText,
} from './capaView';
import CapaNotifyCard from './CapaNotifyCard';

// ── 계정 칸 막기 — 모듈 감시(문서가 살아 있는 동안 내내) ─────────────────────────────
// [data-gw-embed-app]·frame·object·embed 는 무조건, iframe 은 같은 출처(부모 DOM 을 읽을 수 있는 것)만 센다.
const GUARD_HARD = '[data-gw-embed-app], frame, object, embed';
let tainted = false;
let observer = null;
let getTop = () => window.top;
const guardListeners = new Set();

function sameOriginFrame(f) {
  try {
    if (f.hasAttribute('srcdoc')) return true;
    const src = String(f.src || '');
    if (!src || /^(about|blob|javascript):/i.test(src)) return true;
    return new URL(src, window.location.href).origin === window.location.origin;
  } catch (e) {
    return true;
  }
}

function countFrames() {
  let n = document.querySelectorAll(GUARD_HARD).length;
  document.querySelectorAll('iframe').forEach((f) => { if (sameOriginFrame(f)) n += 1; });
  return n;
}

function inFrame() {
  try {
    return window.self !== getTop();
  } catch (e) {
    return true;
  }
}

// 이 탭을 다른 창이 열었다 — 새로고침해도 남는다(따로 판정).
function openedBy() {
  try {
    return window.opener != null;
  } catch (e) {
    return true;
  }
}

// 붙거나 빠진 요소 노드 자신과 그 안을 본다(실제로 붙는 것은 표식을 단 slot div 자신이다).
function nodeHits(node) {
  if (!node || node.nodeType !== 1) return false;
  if (node.matches(GUARD_HARD) || node.querySelector(GUARD_HARD)) return true;
  if (node.matches('iframe') && sameOriginFrame(node)) return true;
  return Array.from(node.querySelectorAll('iframe')).some(sameOriginFrame);
}

function stopWatch() {
  if (!observer) return;
  try { observer.disconnect(); } catch (e) { /* 이미 끊김 */ }
  observer = null;
}

function notifyGuard() {
  guardListeners.forEach((fn) => {
    try { fn(); } catch (e) { /* 다른 구독자는 계속 */ }
  });
}

function taint() {
  tainted = true;
  stopWatch();
  notifyGuard();
}

// 레코드로 판정한다 — 같은 틱에 붙었다 빠진 것도 레코드에 남는다 · 판정 중 예외면 막는다.
function onMutations(records) {
  if (tainted) {
    stopWatch();
    return;
  }
  try {
    const hit = records.some((r) => {
      if (r.type === 'childList') {
        return Array.from(r.addedNodes).some(nodeHits) || Array.from(r.removedNodes).some(nodeHits);
      }
      return r.type === 'attributes' && !!r.target && r.target.nodeType === 1
        && r.target.matches('iframe') && sameOriginFrame(r.target);
    });
    if (hit) taint();
  } catch (e) {
    taint();
  }
}

function startGuard(watch) {
  stopWatch();
  try {
    tainted = countFrames() > 0 || inFrame();
    if (watch && !tainted) {
      observer = new MutationObserver(onMutations);
      observer.observe(document.documentElement, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcdoc'],
      });
    }
  } catch (e) {
    // 감시를 못 세우면 막는 쪽.
    tainted = true;
    stopWatch();
  }
}

startGuard(true);

function subscribeCredGuard(fn) {
  guardListeners.add(fn);
  return () => { guardListeners.delete(fn); };
}

// 'ok' | 'blocked' | 'opener' — 그리기 때도 직접 센다(콜백이 늦거나 놓쳐도) · 세면 그 자리에서 래치한다.
function getCredGuard() {
  if (openedBy()) return 'opener';
  try {
    if (!tainted && countFrames() > 0) {
      tainted = true;
      stopWatch();
    }
  } catch (e) {
    tainted = true;
  }
  return tainted || inFrame() ? 'blocked' : 'ok';
}

/** 시험 전용 — 감시를 다시 세운다(top 을 주면 inFrame 이 그 값을 쓰고, 안 주면 window.top 으로 되돌린다). */
export function __resetCredGuardForTest({ watch = true, top } = {}) {
  getTop = top !== undefined ? () => top : () => window.top;
  startGuard(watch);
  notifyGuard();
}

// ── 화면 ────────────────────────────────────────────────────────────────────
const AUDIT_FIRST = 50;
const AUDIT_MAX = 200;
const NOTE_MAX = 500;
const EMPTY_FORM = { loaded: null, draft: null, warn: {}, conflicts: [], fieldErrors: {} };
const MSG_CONFLICT = '다른 관리자가 먼저 바꾼 칸이 있습니다 — 표시된 칸을 확인하세요';
const MSG_MISMATCH = '다른 관리자가 바꾼 칸과 맞지 않습니다 — 표시된 칸을 확인하세요';
const MSG_NOTHING = '바꿀 칸이 없습니다(다른 관리자가 같은 값으로 바꿨습니다)';
const MSG_BLOCKED = '이 창에서 메신저·메일·드라이브 같은 다른 화면이 열린 적이 있어 계정 입력을 막았습니다(닫아도 숨은 채 남을 수 있습니다). '
  + '이 창에서 연 다른 창·탭(예: «NC 원본 열기»)도 모두 닫고, 새로고침한 뒤 «관리» 탭을 눌러 바로 입력하세요 — 저장 안 한 설정 편집은 사라집니다';
const MSG_OPENER = '이 탭은 다른 창(링크)에서 열려 계정 입력을 막았습니다 — 새로고침으로는 풀리지 않습니다. '
  + '주소창에 주소를 직접 입력해 연 새 탭에서 입력하세요';
const MSG_SEAL = '서버의 계정 봉인 키가 없거나 약합니다 — 운영 담당에게 알려 주세요';
const MSG_NO_RESPONSE = '응답이 없습니다 — 새로 불러온 상태 줄과 감사 기록에서 저장됐는지 확인하세요';
const MSG_ROLE_CHANGED = '권한이 바뀌었습니다 — 다시 로그인하세요';

const FIELD_KIND = {
  active_start: 'text', active_end: 'text', centers: 'lines', region_group: 'text', region_weekday: 'text',
  region_weekend: 'text', region_override_dates: 'lines', business_day_overrides: 'lines',
  threshold_ratio: 'decimal', warning_ratio: 'decimal', holiday_eve_min_streak: 'numeric',
  empty_degree_alert_minutes: 'numeric', alert_max_age_minutes: 'numeric', headless: 'check',
};
const FIELD_HINTS = {
  active_start: 'HH:MM',
  active_end: 'HH:MM(24:00 까지)',
  centers: '한 줄에 하나',
  region_override_dates: '한 줄에 «YYYY-MM-DD 권역»',
  business_day_overrides: '한 줄에 «YYYY-MM-DD 영업» 또는 «YYYY-MM-DD 휴무»',
};

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const hasLabel = (field) => typeof field === 'string' && Object.prototype.hasOwnProperty.call(SETTINGS_LABELS, field);

function myName() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return (u && typeof u.name === 'string' && u.name) || null;
  } catch (e) {
    return null;
  }
}

const section = { ...card, padding: '12px 14px', marginBottom: '12px', overflowWrap: 'anywhere' };
const sectionHead = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px', marginBottom: '8px' };
const sectionTitle = { margin: 0, fontSize: '15px' };
const alertLine = { color: COLORS.dangerText, fontSize: '12px', marginBottom: '6px' };
const mutedLine = { color: COLORS.textSub, fontSize: '12px' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '10px 12px' };
const labelStyle = { display: 'block', fontSize: '12px', color: COLORS.textSub, marginBottom: '4px' };
const fieldInput = { ...input, padding: '7px 10px', fontSize: '13px' };
const th = { padding: '8px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '7px 8px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const disabledLook = (off) => (off ? { opacity: 0.55, cursor: 'not-allowed' } : {});

function Info({ label, children }) {
  return (
    <div style={{ minWidth: 0, fontSize: '13px' }}>
      <div style={{ fontSize: '12px', color: COLORS.textSub }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

export default function CapaAdminTab({ role }) {
  const isAdmin = role === 'admin';
  const guard = useSyncExternalStore(subscribeCredGuard, getCredGuard);
  const blocked = guard !== 'ok';

  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const formRef = useRef(EMPTY_FORM);
  const [settingsLoadMsg, setSettingsLoadMsg] = useState('');
  const [settingsMsg, setSettingsMsg] = useState('');
  const [audit, setAudit] = useState(null);
  const [auditMsg, setAuditMsg] = useState('');
  const [auditLimit, setAuditLimit] = useState(AUDIT_FIRST);
  const auditLimitRef = useRef(AUDIT_FIRST);
  const [auditBusy, setAuditBusy] = useState(false);
  const [busy, setBusy] = useState(null);
  const busyRef = useRef(null);
  const [note, setNote] = useState('');
  const [noteMsg, setNoteMsg] = useState('');
  const [collectMsg, setCollectMsg] = useState('');
  const [idFilled, setIdFilled] = useState(false);
  const [pwFilled, setPwFilled] = useState(false);
  const [credMsg, setCredMsg] = useState('');
  const [credFieldMsg, setCredFieldMsg] = useState({});
  const idRef = useRef(null);
  const pwRef = useRef(null);
  const mountedRef = useRef(true);

  // 폼은 늘 이것으로 고친다 — 렌더 때 붙잡힌 낡은 값을 읽지 않고 결과를 그 자리에서 돌려준다.
  const updateForm = (fn) => {
    const n = fn(formRef.current);
    formRef.current = n;
    if (mountedRef.current) setForm(n);
    return n;
  };

  // 켜기·끄기 응답 · 저장 직전 확인 GET · 첫 로드 — 고친 칸은 남기고 나머지는 새 값으로.
  const receiveSettings = (next) => updateForm((cur) => {
    const m = mergeSettings(cur.loaded, cur.draft, next);
    const warn = {};
    m.conflicts.forEach((field) => {
      warn[field] = `다른 관리자가 ${fmtSettingValue(field, next[field])}으로 바꿨습니다`;
    });
    return { loaded: m.loaded, draft: m.draft, warn, conflicts: m.conflicts, fieldErrors: {} };
  });

  const clearCred = (clearId) => {
    if (pwRef.current) pwRef.current.value = '';
    if (clearId && idRef.current) idRef.current.value = '';
    if (!mountedRef.current) return;
    setPwFilled(false);
    if (clearId) setIdFilled(false);
  };

  const lock = (kind) => {
    if (busyRef.current) return false;
    busyRef.current = kind;
    setBusy(kind);
    return true;
  };

  const unlock = () => {
    busyRef.current = null;
    if (mountedRef.current) setBusy(null);
  };

  useEffect(() => {
    mountedRef.current = true;
    Promise.allSettled([capaAPI.status(), capaAPI.settings(), capaAPI.audit(AUDIT_FIRST)]).then(([s, g, a]) => {
      if (!mountedRef.current) return;
      if (s.status === 'fulfilled' && isObj(s.value?.data)) setStatus(s.value.data);
      else setStatusMsg(s.status === 'rejected' ? errorText(s.reason, '') : '');
      if (g.status === 'fulfilled' && isObj(g.value?.data)) receiveSettings(g.value.data);
      else setSettingsLoadMsg(g.status === 'rejected' ? errorText(g.reason, '') : '');
      if (a.status === 'fulfilled' && Array.isArray(a.value?.data)) setAudit(a.value.data);
      else setAuditMsg(a.status === 'rejected' ? errorText(a.reason, '감사 기록을 불러오지 못했습니다.') : '감사 기록을 불러오지 못했습니다.');
      setReady(true);
    });
    return () => { mountedRef.current = false; };
  }, []);

  // 막히는 순간 두 칸을 비운다(렌더 중이 아니라 그린 직후).
  useLayoutEffect(() => {
    if (guard !== 'ok') clearCred(true);
  }, [guard]);

  // 쓰기 뒤 — 상태·감사만 다시 부른다(설정은 쓰기 응답으로 받는다). 실패하면 앞 값을 두고 문구만.
  const refresh = async () => {
    const [s, a] = await Promise.allSettled([capaAPI.status(), capaAPI.audit(auditLimitRef.current)]);
    if (!mountedRef.current) return;
    if (s.status === 'fulfilled' && isObj(s.value?.data)) {
      setStatus(s.value.data);
      setStatusMsg('');
    } else {
      setStatusMsg(s.status === 'rejected' ? errorText(s.reason, '상태를 다시 불러오지 못했습니다.') : '상태를 다시 불러오지 못했습니다.');
    }
    if (a.status === 'fulfilled' && Array.isArray(a.value?.data)) {
      setAudit(a.value.data);
      setAuditMsg('');
    } else {
      setAuditMsg(a.status === 'rejected' ? errorText(a.reason, '감사 기록을 다시 불러오지 못했습니다.') : '감사 기록을 다시 불러오지 못했습니다.');
    }
  };

  const settings = form.loaded;
  const enabledNow = settings ? !!settings.enabled : (status ? !!status.enabled : null);
  const cred = status?.credentials || null;
  const credSet = cred?.set === true;

  // ── ① 켜기·끄기 ──
  const collectError = (detail, fallback) => {
    if (isObj(detail) && detail.field === 'reason') setNoteMsg(detailText(detail, fallback));
    else setCollectMsg(detailText(detail, fallback));
  };

  const onEnable = async () => {
    if (!isAdmin || !credSet || !note.trim()) return;
    if (!lock('enable')) return;
    setCollectMsg('');
    setNoteMsg('');
    try {
      const ok = await confirmDialog('수집을 켭니다. 네이버 운영 화면에 2분마다 접속합니다. 계속할까요?');
      if (!ok || !mountedRef.current) return;
      const r = await capaAPI.enable(note.trim());
      if (!mountedRef.current) return;
      if (isObj(r?.data)) receiveSettings(r.data);
      setNote('');
      await refresh();
    } catch (err) {
      if (mountedRef.current) collectError(err?.response?.data?.detail, '수집을 켜지 못했습니다.');
    } finally {
      unlock();
    }
  };

  const onDisable = async () => {
    if (!isAdmin) return;
    if (!lock('disable')) return;
    setCollectMsg('');
    setNoteMsg('');
    try {
      const ok = await confirmDialog('수집을 끕니다. 계속할까요?');
      if (!ok || !mountedRef.current) return;
      const r = await capaAPI.disable(note.trim() || undefined);
      if (!mountedRef.current) return;
      if (isObj(r?.data)) receiveSettings(r.data);
      setNote('');
      await refresh();
    } catch (err) {
      if (mountedRef.current) collectError(err?.response?.data?.detail, '수집을 끄지 못했습니다.');
    } finally {
      unlock();
    }
  };

  // ── ② 계정 저장 ──
  const onSaveCred = async () => {
    if (!isAdmin || busyRef.current) return;
    // 저장 직전 재검사 — 감시가 놓쳤어도 지금 붙어 있으면 보내지 않는다.
    if (openedBy() || inFrame() || countFrames() > 0) {
      taint();
      clearCred(true);
      return;
    }
    if (blocked || !idRef.current || !pwRef.current) return;
    if (!lock('cred')) return;
    setCredMsg('');
    setCredFieldMsg({});
    try {
      let outcome;
      const id = idRef.current.value;
      const pw = pwRef.current.value;
      try {
        const r = await capaAPI.putCredentials(id, pw);
        outcome = { ok: true, data: isObj(r?.data) ? r.data : {} };
      } catch (err) {
        outcome = { ok: false, http: err?.response?.status, detail: err?.response?.data?.detail, none: !err?.response };
      } finally {
        clearCred(false);
      }
      if (!mountedRef.current) return;
      if (outcome.ok) {
        clearCred(true);
        const data = outcome.data;
        setStatus((prev) => (prev ? {
          ...prev,
          credentials: {
            ...(prev.credentials || {}),
            set: true,
            login_id_hint: data.login_id_hint ?? null,
            updated_at: data.updated_at ?? null,
            login_failed_at: null,
            updated_by_name: myName(),
          },
        } : prev));
        await refresh();
        return;
      }
      if (outcome.http === 403) {
        clearCred(true);
        setCredMsg(MSG_ROLE_CHANGED);
        return;
      }
      const d = outcome.detail;
      if (isObj(d) && d.code === 'too_long' && (d.field === 'login_id' || d.field === 'password')) {
        setCredFieldMsg({ [d.field]: detailText(d, '너무 깁니다.') });
      } else if (isObj(d) && (d.code === 'seal_key_missing' || d.code === 'seal_key_weak')) {
        setCredMsg(MSG_SEAL);
      } else if (outcome.none) {
        await refresh();
        if (mountedRef.current) setCredMsg(MSG_NO_RESPONSE);
      } else {
        setCredMsg(detailText(d, '계정을 저장하지 못했습니다.'));
      }
    } finally {
      unlock();
    }
  };

  // ── ③ 설정 ──
  const baseDraft = settings ? settingsDraft(settings) : null;
  const draft = form.draft;
  const dirty = !!(baseDraft && draft && SETTINGS_FIELDS.some((f) => draft[f] !== baseDraft[f]));
  const diff = settings && draft ? settingsDiff(settings, draft) : null;
  const settingsBusy = busy === 'settings';

  const settingsError = (detail, fallback) => {
    if (isObj(detail) && detail.code === 'bad_input' && hasLabel(detail.field)) {
      const field = detail.field;
      updateForm((cur) => ({ ...cur, fieldErrors: { [field]: `${SETTINGS_LABELS[field]}: 값을 확인하세요` } }));
      return;
    }
    setSettingsMsg(detailText(detail, fallback));
  };

  const onSaveSettings = async () => {
    if (!isAdmin) return;
    if (!lock('settings')) return;
    setSettingsMsg('');
    try {
      let next = null;
      try {
        const r = await capaAPI.settings();
        next = isObj(r?.data) ? r.data : null;
      } catch (err) {
        if (mountedRef.current) setSettingsMsg(detailText(err?.response?.data?.detail, '설정을 다시 확인하지 못해 저장하지 않았습니다.'));
        return;
      }
      if (!mountedRef.current) return;
      if (!next) {
        setSettingsMsg('설정을 다시 확인하지 못해 저장하지 않았습니다.');
        return;
      }
      const m = receiveSettings(next);
      if (m.conflicts.length > 0) return;
      const d = settingsDiff(m.loaded, m.draft);
      if (d.errors.length > 0) {
        setSettingsMsg(MSG_MISMATCH);
        return;
      }
      if (Object.keys(d.changes).length === 0) {
        setSettingsMsg(MSG_NOTHING);
        return;
      }
      let saved = null;
      try {
        const r = await capaAPI.putSettings(d.changes);
        saved = isObj(r?.data) ? r.data : null;
      } catch (err) {
        if (mountedRef.current) settingsError(err?.response?.data?.detail, '설정을 저장하지 못했습니다.');
        return;
      }
      if (!mountedRef.current) return;
      if (saved) updateForm(() => ({ loaded: saved, draft: settingsDraft(saved), warn: {}, conflicts: [], fieldErrors: {} }));
      await refresh();
    } finally {
      unlock();
    }
  };

  const onRevert = () => {
    setSettingsMsg('');
    updateForm((cur) => ({ loaded: cur.loaded, draft: settingsDraft(cur.loaded), warn: {}, conflicts: [], fieldErrors: {} }));
  };

  const onField = (field, value) => {
    updateForm((cur) => ({ ...cur, draft: { ...cur.draft, [field]: value } }));
  };

  const onReload = async () => {
    if (dirty) {
      const ok = await confirmDialog('저장 안 한 설정 편집이 사라집니다. 새로고침할까요?');
      if (!ok) return;
    }
    window.location.reload();
  };

  // ── ⑤ 감사 ──
  const onMoreAudit = async () => {
    if (auditBusy) return;
    setAuditBusy(true);
    try {
      const r = await capaAPI.audit(AUDIT_MAX);
      if (!mountedRef.current) return;
      auditLimitRef.current = AUDIT_MAX;
      setAuditLimit(AUDIT_MAX);
      setAudit(Array.isArray(r?.data) ? r.data : []);
      setAuditMsg('');
    } catch (err) {
      if (mountedRef.current) setAuditMsg(errorText(err, '감사 기록을 불러오지 못했습니다.'));
    } finally {
      if (mountedRef.current) setAuditBusy(false);
    }
  };

  // ── 그리기 ──
  const last = status?.last_snapshot || null;
  let snapText = DASH;
  if (status && !last) snapText = '아직 수집된 값이 없습니다';
  if (status && last) {
    snapText = [
      fmtKstDateTime(last.collected_at) || DASH,
      snapshotStatusText(last.status),
      `단계 ${last.step || DASH}`,
      `오류 ${last.error_kind || DASH}`,
      `행 ${isNum(last.row_count) ? last.row_count : DASH}`,
    ].join(' · ');
  }
  let enabledLabel = '알 수 없음';
  if (enabledNow === true) enabledLabel = '켜짐';
  if (enabledNow === false) enabledLabel = '꺼짐';

  let enableHint = '';
  if (isAdmin && ready && !status) enableHint = '상태를 불러오지 못해 켤 수 없습니다';
  else if (isAdmin && ready && !credSet) enableHint = '네이버 계정을 먼저 저장하세요';
  const canEnable = isAdmin && !busy && credSet && note.trim() !== '';
  const noteOff = !isAdmin || !!busy;

  let credLine = '불러오는 중…';
  if (ready && !status) credLine = '상태를 불러오지 못했습니다';
  if (status) {
    if (!credSet) credLine = '설정 안 됨';
    else if (isAdmin) {
      credLine = `설정됨 · ${cred.login_id_hint || DASH} · ${fmtKstDateTime(cred.updated_at) || DASH} · ${cred.updated_by_name || DASH}`;
    } else credLine = '설정됨';
  }
  const credInputOff = !isAdmin || blocked || !!busy;
  const canSaveCred = isAdmin && !blocked && !busy && idFilled && pwFilled;

  const canSaveSettings = isAdmin && !busy && !!diff && diff.errors.length === 0 && Object.keys(diff.changes).length > 0;
  const canRevert = isAdmin && !busy && dirty;
  const settingInputOff = !isAdmin || settingsBusy;

  const renderField = (field) => {
    const kind = FIELD_KIND[field];
    const id = `capa-set-${field}`;
    const messages = [];
    (diff ? diff.errors : []).filter((e) => e.field === field)
      .forEach((e) => messages.push(e.line ? `${e.line}번째 줄: ${e.message}` : e.message));
    if (form.fieldErrors[field]) messages.push(form.fieldErrors[field]);
    if (form.warn[field]) messages.push(form.warn[field]);
    let control;
    if (kind === 'check') {
      control = (
        <input id={id} type="checkbox" checked={!!draft[field]} disabled={settingInputOff}
          onChange={(ev) => onField(field, ev.target.checked)} />
      );
    } else if (kind === 'lines') {
      control = (
        <textarea id={id} rows={3} value={draft[field] ?? ''} disabled={settingInputOff} placeholder={FIELD_HINTS[field] || ''}
          onChange={(ev) => onField(field, ev.target.value)}
          style={{ ...fieldInput, resize: 'vertical', fontFamily: 'inherit', ...disabledLook(settingInputOff) }} />
      );
    } else {
      let inputMode;
      if (kind === 'decimal') inputMode = 'decimal';
      if (kind === 'numeric') inputMode = 'numeric';
      control = (
        <input id={id} type="text" inputMode={inputMode} value={draft[field] ?? ''} disabled={settingInputOff}
          placeholder={FIELD_HINTS[field] || ''} onChange={(ev) => onField(field, ev.target.value)}
          style={{ ...fieldInput, ...disabledLook(settingInputOff) }} />
      );
    }
    return (
      <div key={field} style={{ minWidth: 0 }}>
        {kind === 'check' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', paddingTop: '18px' }}>
            {control}
            <label htmlFor={id}>{SETTINGS_LABELS[field]}</label>
          </div>
        ) : (
          <>
            <label htmlFor={id} style={labelStyle}>{SETTINGS_LABELS[field]}</label>
            {control}
          </>
        )}
        {messages.length > 0 && (
          <div data-testid={`capa-set-msg-${field}`} style={{ ...alertLine, margin: '4px 0 0' }}>
            {messages.map((text, i) => <div key={i}>{text}</div>)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="capa-admin">
      {!isAdmin && (
        <div data-testid="capa-admin-readonly" style={{ ...section, background: COLORS.warnSoft, color: COLORS.warn, fontSize: '13px' }}>
          변경은 시스템 관리자만 할 수 있습니다
        </div>
      )}

      {/* ① 수집 상태 */}
      <section aria-labelledby="capa-admin-collect-title" data-testid="capa-admin-collect" style={section}>
        <div style={sectionHead}>
          <h3 id="capa-admin-collect-title" style={sectionTitle}>수집 상태</h3>
          {ready && (
            <span data-testid="capa-admin-enabled" style={badge(enabledNow === true ? 'success' : (enabledNow === false ? 'default' : 'warn'))}>
              {enabledLabel}
            </span>
          )}
          {status?.collector_down && <span data-testid="capa-admin-down" style={badge('danger')}>수집기 이상</span>}
        </div>
        {!ready && <div role="status" style={mutedLine}>불러오는 중…</div>}
        {ready && !status && (
          <div role="alert" data-testid="capa-admin-status-fail" style={alertLine}>
            상태를 불러오지 못했습니다{statusMsg ? ` — ${statusMsg}` : ''}
          </div>
        )}
        {status && statusMsg && <div role="alert" style={alertLine}>{statusMsg}</div>}
        {collectMsg && <div role="alert" data-testid="capa-admin-collect-msg" style={alertLine}>{collectMsg}</div>}
        {enabledNow === true && status && cred?.set === false && (
          <div data-testid="capa-admin-no-cred" style={{ ...alertLine, fontWeight: 600 }}>
            켜져 있지만 네이버 계정이 없어 수집하지 않습니다 — 아래에서 계정을 저장하세요
          </div>
        )}
        <div style={{ ...grid, marginBottom: '8px' }}>
          <Info label="마지막 켬/끔 근거">{settings?.enabled_reason || DASH}</Info>
          <Info label="마지막 변경">{`${fmtKstDateTime(settings?.updated_at) || DASH} · ${settings?.updated_by_name || DASH}`}</Info>
          <Info label="수집 주기">{isNum(status?.interval_seconds) ? `${status.interval_seconds / 60}분 고정` : DASH}</Info>
          <Info label="실제 간격(최근 10회 평균)">{isNum(status?.avg_interval_ms) ? `${(status.avg_interval_ms / 1000).toFixed(1)}초` : DASH}</Info>
          <Info label="최근 스냅샷"><span data-testid="capa-admin-snapshot">{snapText}</span></Info>
        </div>

        {enabledNow === false && (
          <div>
            <label htmlFor="capa-admin-note" style={labelStyle}>켜는 근거(필수)</label>
            <textarea id="capa-admin-note" rows={2} maxLength={NOTE_MAX} value={note} disabled={noteOff}
              onChange={(ev) => setNote(ev.target.value)}
              style={{ ...fieldInput, resize: 'vertical', fontFamily: 'inherit', ...disabledLook(noteOff) }} />
            <div style={{ ...mutedLine, textAlign: 'right' }}>{note.length}/{NOTE_MAX}자</div>
            {noteMsg && <div role="alert" data-testid="capa-admin-note-msg" style={alertLine}>{noteMsg}</div>}
            {enableHint && <div data-testid="capa-admin-enable-hint" style={alertLine}>{enableHint}</div>}
            <button type="button" onClick={onEnable} disabled={!canEnable}
              style={{ ...btn('primary', 'sm'), ...disabledLook(!canEnable) }}>
              수집 켜기
            </button>
          </div>
        )}
        {enabledNow === true && (
          <div>
            <label htmlFor="capa-admin-note" style={labelStyle}>끄는 근거(선택)</label>
            <textarea id="capa-admin-note" rows={2} maxLength={NOTE_MAX} value={note} disabled={noteOff}
              onChange={(ev) => setNote(ev.target.value)}
              style={{ ...fieldInput, resize: 'vertical', fontFamily: 'inherit', ...disabledLook(noteOff) }} />
            <div style={{ ...mutedLine, textAlign: 'right' }}>{note.length}/{NOTE_MAX}자</div>
            {noteMsg && <div role="alert" data-testid="capa-admin-note-msg" style={alertLine}>{noteMsg}</div>}
            <button type="button" onClick={onDisable} disabled={!isAdmin || !!busy}
              style={{ ...btn('danger', 'sm'), ...disabledLook(!isAdmin || !!busy) }}>
              수집 끄기
            </button>
          </div>
        )}
      </section>

      {/* ② 네이버 계정 */}
      <section aria-labelledby="capa-admin-cred-title" data-testid="capa-admin-cred" style={section}>
        <div style={sectionHead}>
          <h3 id="capa-admin-cred-title" style={sectionTitle}>네이버 계정</h3>
        </div>
        {credMsg && <div role="alert" data-testid="capa-admin-cred-msg" style={alertLine}>{credMsg}</div>}
        <div data-testid="capa-admin-cred-state" style={{ fontSize: '13px', marginBottom: '6px' }}>{credLine}</div>
        {isAdmin && cred?.login_failed_at && (
          <div data-testid="capa-admin-login-failed" style={alertLine}>
            로그인 실패로 수집이 멈춤({fmtKstDateTime(cred.login_failed_at) || DASH}) — 계정을 다시 저장하면 풀립니다
          </div>
        )}
        {isAdmin && blocked && (
          <div role="alert" data-testid="capa-admin-cred-blocked" style={{ ...alertLine, background: COLORS.dangerSoft, padding: '8px 10px', borderRadius: '8px' }}>
            <div>{guard === 'opener' ? MSG_OPENER : MSG_BLOCKED}</div>
            {guard !== 'opener' && (
              <button type="button" onClick={onReload} style={{ ...btn('secondary', 'sm'), marginTop: '6px' }}>새로고침</button>
            )}
          </div>
        )}
        <div style={{ ...grid, marginBottom: '6px' }}>
          <div style={{ minWidth: 0 }}>
            <label htmlFor="capa-naver-cred-1" style={labelStyle}>아이디</label>
            <input ref={idRef} id="capa-naver-cred-1" name="capa-naver-cred-1" type="text" autoComplete="off" maxLength={100}
              data-lpignore="true" data-1p-ignore="true" disabled={credInputOff}
              onInput={(ev) => setIdFilled(ev.currentTarget.value.trim() !== '')}
              style={{ ...fieldInput, ...disabledLook(credInputOff) }} />
            {credFieldMsg.login_id && <div data-testid="capa-admin-cred-login_id-msg" style={{ ...alertLine, marginTop: '4px' }}>{credFieldMsg.login_id}</div>}
          </div>
          <div style={{ minWidth: 0 }}>
            <label htmlFor="capa-naver-cred-2" style={labelStyle}>비밀번호</label>
            <input ref={pwRef} id="capa-naver-cred-2" name="capa-naver-cred-2" type="password" autoComplete="new-password"
              data-lpignore="true" data-1p-ignore="true" disabled={credInputOff}
              onInput={(ev) => setPwFilled(ev.currentTarget.value !== '')}
              style={{ ...fieldInput, ...disabledLook(credInputOff) }} />
            {credFieldMsg.password && <div data-testid="capa-admin-cred-password-msg" style={{ ...alertLine, marginTop: '4px' }}>{credFieldMsg.password}</div>}
          </div>
        </div>
        <div style={{ ...mutedLine, marginBottom: '6px' }}>브라우저가 비밀번호 저장을 묻거나 비밀번호를 제안하면 누르지 마세요</div>
        <button type="button" onClick={onSaveCred} disabled={!canSaveCred}
          style={{ ...btn('primary', 'sm'), ...disabledLook(!canSaveCred) }}>
          계정 저장
        </button>
      </section>

      {/* ③ 설정 */}
      <section aria-labelledby="capa-admin-settings-title" data-testid="capa-admin-settings" style={section}>
        <div style={sectionHead}>
          <h3 id="capa-admin-settings-title" style={sectionTitle}>설정</h3>
        </div>
        {form.conflicts.length > 0 && (
          <div role="alert" data-testid="capa-admin-settings-conflict" style={alertLine}>{MSG_CONFLICT}</div>
        )}
        {settingsMsg && <div role="alert" data-testid="capa-admin-settings-msg" style={alertLine}>{settingsMsg}</div>}
        {!ready && <div role="status" style={mutedLine}>불러오는 중…</div>}
        {ready && !settings && (
          <div role="alert" data-testid="capa-admin-settings-fail" style={alertLine}>
            설정을 불러오지 못했습니다{settingsLoadMsg ? ` — ${settingsLoadMsg}` : ''}
          </div>
        )}
        {settings && draft && (
          <>
            <div style={grid}>{SETTINGS_FIELDS.map(renderField)}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
              <button type="button" onClick={onSaveSettings} disabled={!canSaveSettings}
                style={{ ...btn('primary', 'sm'), ...disabledLook(!canSaveSettings) }}>
                설정 저장
              </button>
              <button type="button" onClick={onRevert} disabled={!canRevert}
                style={{ ...btn('secondary', 'sm'), ...disabledLook(!canRevert) }}>
                되돌리기
              </button>
            </div>
          </>
        )}
      </section>

      {/* ④ 알림 대상 */}
      <CapaNotifyCard isAdmin={isAdmin} lock={lock} unlock={unlock} busy={busy} onChanged={refresh} />

      {/* ⑤ 감사 기록 */}
      <section aria-labelledby="capa-admin-audit-title" data-testid="capa-admin-audit" style={section}>
        <div style={sectionHead}>
          <h3 id="capa-admin-audit-title" style={sectionTitle}>감사 기록</h3>
        </div>
        {auditMsg && <div role="alert" data-testid="capa-admin-audit-msg" style={alertLine}>{auditMsg}</div>}
        {!ready && <div role="status" style={mutedLine}>불러오는 중…</div>}
        {audit !== null && (
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="감사 기록" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
              <thead><tr>{['시각', '사람', '동작', '내용'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {audit.map((entry, i) => {
                  const text = auditText(entry);
                  let who = DASH;
                  if (entry?.user_name) who = entry.user_name;
                  else if (entry?.user_id !== null && entry?.user_id !== undefined) who = `#${entry.user_id}`;
                  return (
                    <tr key={entry?.id ?? `a-${i}`} data-testid="capa-audit-row">
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtKstDateTime(entry?.created_at) || DASH}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{who}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{text.action}</td>
                      <td style={{ ...td, minWidth: '240px' }}>{text.detail}</td>
                    </tr>
                  );
                })}
                {audit.length === 0 && (
                  <tr><td colSpan={4} style={{ ...td, color: COLORS.textMuted, textAlign: 'center' }}>감사 기록이 없습니다</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {ready && auditLimit < AUDIT_MAX && (
          <button type="button" onClick={onMoreAudit} disabled={auditBusy}
            style={{ ...btn('secondary', 'sm'), marginTop: '8px', ...disabledLook(auditBusy) }}>
            200건까지 보기
          </button>
        )}
      </section>
    </div>
  );
}
